import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type FlowStep = 'airline' | 'consignee' | 'customs' | 'delivery' | 'payment';
export type EoriOption = 'own' | 'airgo' | 'other';

export interface AirlineFormData {
  origin: string;
  destination: string;
  departureDate: string;
  weightKg: string;
  pieces: string;
  description: string;
  contactName: string;
  contactEmail: string;
}

export interface ConsigneeFormData {
  eoriOption: EoriOption;
  eoriNumber: string;
  companyName: string;
  contactName: string;
  contactPhone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
}

export interface CustomsFormData {
  hsCode: string;
  declaredValue: string;
  currency: string;
  eoriNumber: string;
  description: string;
}

export interface DeliveryFormData {
  recipientName: string;
  recipientPhone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  instructions: string;
}

export interface ServicesSelected {
  airline: boolean;
  customs: boolean;
  delivery: boolean;
}

export const SERVICE_PRICES = {
  airline: 580,
  customs: 75,
  delivery: 125,
  dutyVat: 1032,
} as const;

interface BookingFlowState {
  currentStep: FlowStep;
  servicesSelected: ServicesSelected;
  awbReference: string | null;
  customsReference: string | null;
  bookingFlowId: string | null;
  formData: {
    airline?: AirlineFormData;
    consignee?: ConsigneeFormData;
    customs?: CustomsFormData;
    delivery?: DeliveryFormData;
  };
  pendingSkip: FlowStep | null;
  isSandbox: boolean;
}

const initialState: BookingFlowState = {
  currentStep: 'airline',
  servicesSelected: { airline: true, customs: true, delivery: true },
  awbReference: null,
  customsReference: null,
  bookingFlowId: null,
  formData: {},
  pendingSkip: null,
  isSandbox: false,
};

function generateRef(prefix: string): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substr(2, 4).toUpperCase();
  return `${prefix}-${ts}-${rand}`;
}

function nextStepAfter(step: FlowStep, services: ServicesSelected): FlowStep {
  if (step === 'airline') return 'consignee';
  if (step === 'consignee') {
    if (services.customs) return 'customs';
    if (services.delivery) return 'delivery';
    return 'payment';
  }
  if (step === 'customs') {
    if (services.delivery) return 'delivery';
    return 'payment';
  }
  return 'payment';
}

const bookingFlowSlice = createSlice({
  name: 'bookingFlow',
  initialState,
  reducers: {
    resetFlow: () => initialState,

    setSandbox(state, action: PayloadAction<boolean>) {
      state.isSandbox = action.payload;
    },

    submitAirline(state, action: PayloadAction<AirlineFormData>) {
      state.formData.airline = action.payload;
      state.awbReference = generateRef(state.isSandbox ? 'DEMO' : 'AIRGO');
      state.currentStep = 'consignee';
    },

    submitConsignee(state, action: PayloadAction<ConsigneeFormData>) {
      state.formData.consignee = action.payload;
      state.currentStep = nextStepAfter('consignee', state.servicesSelected);
    },

    submitCustoms(state, action: PayloadAction<CustomsFormData>) {
      state.formData.customs = action.payload;
      state.customsReference = generateRef('CUST');
      state.currentStep = nextStepAfter('customs', state.servicesSelected);
    },

    submitDelivery(state, action: PayloadAction<DeliveryFormData>) {
      state.formData.delivery = action.payload;
      state.currentStep = 'payment';
    },

    requestSkip(state, action: PayloadAction<FlowStep>) {
      state.pendingSkip = action.payload;
    },

    cancelSkip(state) {
      state.pendingSkip = null;
    },

    confirmSkip(state) {
      const step = state.pendingSkip;
      if (!step) return;
      state.pendingSkip = null;
      if (step === 'customs') {
        state.servicesSelected.customs = false;
        state.currentStep = state.servicesSelected.delivery ? 'delivery' : 'payment';
      } else if (step === 'delivery') {
        state.servicesSelected.delivery = false;
        state.currentStep = 'payment';
      }
    },

    goToStep(state, action: PayloadAction<FlowStep>) {
      state.currentStep = action.payload;
    },

    setBookingFlowId(state, action: PayloadAction<string>) {
      state.bookingFlowId = action.payload;
    },
  },
});

export const {
  resetFlow,
  setSandbox,
  submitAirline,
  submitConsignee,
  submitCustoms,
  submitDelivery,
  requestSkip,
  cancelSkip,
  confirmSkip,
  goToStep,
  setBookingFlowId,
} = bookingFlowSlice.actions;

export default bookingFlowSlice.reducer;
